import axios from 'axios';
import { differenceInCalendarDays, isBefore, startOfDay, subMonths, subWeeks } from 'date-fns';
import { is_standard_timespan } from './user_options';
import { contentLengthSeconds } from './content_length';

const personalEmailDomains: string[] = [
  'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'aol.com',
  'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'gmx.com', 'me.com', 'msn.com', 'live.com', 'mac.com', 'fastmail.com',
];

const singleSignOnDomains: string[] = ['prismahealth.org', 'stjude.org'];

function getDayOfYear(): number {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now.getTime() - start.getTime() + (start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000;
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
}

function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function shouldShowSurvey(lastSurveyTimestamp: string | number | Date): boolean {
  const currentTime = new Date();
  const lastSurveyTime = new Date(lastSurveyTimestamp as any);
  const daysSinceSurvey = Math.floor((currentTime.getTime() - lastSurveyTime.getTime()) / (1000 * 60 * 60 * 24));
  const surveyChance = Math.min(daysSinceSurvey * 10, 100);
  const dayOfYear = getDayOfYear();
  const seed = dayOfYear + lastSurveyTime.getTime();
  const randomChance = seededRandom(seed) * 100;
  return randomChance < surveyChance;
}

function shuffleArrayWithSeed<T>(array: T[]): T[] {
  const seed = getDayOfYear();
  const result = array.slice();
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(seed + i) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** FNV-1a 32-bit over UTF-16 code units, unsigned. */
function hashString(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** A stable index into a list of `length` items for `seed`; -1 for an empty list. */
function seededIndex(seed: string, length: number): number {
  if (!(length > 0)) return -1;
  return hashString(seed) % length;
}

function shuffleArray<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

const dateFormatter = (dateString = ''): Date | undefined => {
  const re = /(\d{4})-(\d{2})-(\d{2})(\s|T)(\d{2}):(\d{2}):(\d{2})/;
  const result = dateString.match(re);
  if (result) {
    return new Date(
      parseInt(result[1]), parseInt(result[2]) - 1, parseInt(result[3]),
      parseInt(result[5]), parseInt(result[6])
    );
  }
};

const formatUnixTimestamp = (unixTimestamp: number): string => {
  const date = new Date(Number(unixTimestamp));
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

function formatIsoDate(isoStr: string): string {
  if (!isoStr) return '';
  const [datePart] = isoStr.split('T');
  const [year, month, day] = datePart.split('-');
  return `${month}/${day}/${year}`;
}

function formatIsoDateForFilename(isoStr: string): string {
  if (!isoStr) return '';
  const [datePart] = isoStr.split('T');
  const [year, month, day] = datePart.split('-');
  return `${year}-${month}-${day}`;
}

function isPersonalEmail(email: string): boolean {
  return personalEmailDomains.some((domain) => email.endsWith(`@${domain}`));
}

function isSingleSignOnEmail(email: string): boolean {
  return singleSignOnDomains.some((domain) => email.endsWith(`@${domain}`));
}

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

interface CheckEmailResult {
  email_in_use: boolean;
  sso: boolean;
  sso_link: string | null;
  // Only when an account exists: false while it is still waiting to be confirmed.
  confirmed?: boolean;
}

// Asks the backend whether an account exists for this email and, if so, whether
// its group logs in through SSO and whether it has been confirmed. Throws on
// network/server errors.
async function checkEmail(email: string): Promise<CheckEmailResult> {
  const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/check_email`, { email });
  const { email_in_use = false, sso = false, sso_link = null, confirmed } = response.data ?? {};
  return { email_in_use, sso, sso_link, confirmed };
}

// returns an RGB array
function interpolateColor(color1: number[], color2: number[], factor = 0.5): number[] {
  const result = color1.slice();
  for (let i = 0; i < 3; i++) {
    result[i] = Math.round(result[i] + factor * (color2[i] - result[i]));
  }
  return result;
}

function round(value: number, precision?: number): number {
  const multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
}

function componentToHex(c: number): string {
  const hex = c.toString(16);
  return hex.length === 1 ? '0' + hex : hex;
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

function getColorGradient(percent: number, color1: number[], color2: number[], color3: number[]): string {
  let startColor: number[], endColor: number[], adjustedPercent: number;
  if (percent <= 0.5) {
    startColor = color1; endColor = color2; adjustedPercent = percent * 2;
  } else {
    startColor = color2; endColor = color3; adjustedPercent = (percent - 0.5) * 2;
  }
  const interpolatedColor = interpolateColor(startColor, endColor, adjustedPercent);
  return rgbToHex(interpolatedColor[0], interpolatedColor[1], interpolatedColor[2]);
}

function hexToRgba(hex: string, alpha: number): number[] {
  hex = hex.replace(/^#/, '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return [r, g, b, alpha];
}

function sumContentLengths(content: Array<{ length?: string }>): string {
  let totalSeconds = 0;
  content.forEach((c) => {
    totalSeconds += contentLengthSeconds(c?.length) ?? 0;
  });
  const totalMinutes = Math.floor(totalSeconds / 60) % 60;
  const totalHours = Math.floor(Math.floor(totalSeconds / 60) / 60);
  const remainingSeconds = totalSeconds % 60;
  const formattedMinutes = String(totalMinutes).padStart(2, '0');
  const formattedSeconds = String(remainingSeconds).padStart(2, '0');
  return `${totalHours > 0 ? String(totalHours) + ':' : ''}${formattedMinutes}:${formattedSeconds}`;
}

// 0 for a length it can't read. Handouts have blank lengths, so that's no error.
function convertTimeToSeconds(timeString: unknown): number {
  return contentLengthSeconds(timeString) ?? 0;
}

const convertSecondsToTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min`;
};

const hasCommonItem = <T>(arr1: T[], arr2: T[]): boolean => arr1.some((item) => arr2.includes(item));

const decodeEntities = (function () {
  const element = document.createElement('div');
  function decodeHTMLEntities(str: string | null | undefined): string | null | undefined {
    if (str && typeof str === 'string') {
      str = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gmi, '');
      str = str.replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gmi, '');
      element.innerHTML = str;
      str = element.textContent ?? '';
      element.textContent = '';
    }
    return str;
  }
  return decodeHTMLEntities;
})();

const breakpoints = { xs: 0, sm: 576, md: 768, lg: 992, xl: 1200, xxl: 1400 };

const getCurrentBreakpoint = (): string => {
  const width = window.innerWidth;
  if (width >= breakpoints.xxl) return 'xxl';
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
};

const forceReloadWithCacheBust = (): void => {
  const hasReloaded = sessionStorage.getItem('hasReloaded');
  if (!hasReloaded) {
    sessionStorage.setItem('hasReloaded', 'true');
    const cacheBustValue = process.env.REACT_APP_COMMIT_ID || 'v1';
    const url = new URL(window.location.href);
    url.searchParams.set('cache-bust', cacheBustValue);
    window.location.replace(url.toString());
  } else {
    console.warn('Already reloaded once — not forcing another refresh.');
  }
};

function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return 'Mobile';
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'Tablet';
  return 'Desktop';
}

interface TimespanFilters {
  timespan?: string;
  start_date?: string | null;
  end_date?: string | null;
  [key: string]: unknown;
}

function parseTimespanFilter(filters: TimespanFilters): { start_date: string | null; end_date: string | null } {
  let start_date: Date | null = null;
  const today = new Date();
  let end_date: Date = new Date(today);

  if (!is_standard_timespan(filters.timespan)) {
    return { start_date: filters.start_date ?? null, end_date: filters.end_date ?? null };
  }
  switch (filters.timespan) {
    case '7 Days':   start_date = startOfDay(new Date(today.setDate(today.getDate() - 6))); break;
    case '30 Days':  start_date = startOfDay(new Date(new Date().setDate(new Date().getDate() - 29))); break;
    case '90 Days':  start_date = startOfDay(new Date(new Date().setDate(new Date().getDate() - 89))); break;
    case '4 Months': start_date = startOfDay(new Date(new Date().setMonth(new Date().getMonth() - 4))); break;
    case '5 Months': start_date = startOfDay(new Date(new Date().setMonth(new Date().getMonth() - 5))); break;
    case '6 Months': start_date = startOfDay(new Date(new Date().setMonth(new Date().getMonth() - 6))); break;
    case '4-6 Months':
      start_date = startOfDay(new Date(new Date().setMonth(new Date().getMonth() - 6)));
      end_date = startOfDay(new Date(new Date().setMonth(new Date().getMonth() - 4)));
      break;
    case '1 Year':   start_date = startOfDay(new Date(new Date().setDate(new Date().getDate() - 364))); break;
    default:         start_date = null;
  }
  return {
    start_date: start_date ? startOfDay(start_date).toISOString().slice(0, 10) : null,
    end_date: end_date ? startOfDay(end_date).toISOString().slice(0, 10) : null,
  };
}

function parseAgeFilter(age: string): { start_age: number | null; end_age: number | null } {
  let start_age: number | null;
  let end_age: number | null;
  switch (age) {
    case 'Under 28': start_age = null; end_age = 27; break;
    case '28-34':    start_age = 28; end_age = 34; break;
    case '35-44':    start_age = 35; end_age = 44; break;
    case '45-54':    start_age = 45; end_age = 54; break;
    case '55-69':    start_age = 55; end_age = 69; break;
    case 'Over 69':  start_age = 70; end_age = null; break;
    default:         start_age = null; end_age = null;
  }
  return { start_age, end_age };
}

function showBurnoutQuestions(
  lastBurnoutLevelResponse: unknown,
  lastBurnoutStressorResponse: unknown
): { showLevelQuestion: boolean; showStressorQuestion: boolean } {
  const levelTimestamp = new Date(lastBurnoutLevelResponse as any);
  const stressorTimestamp = new Date(lastBurnoutStressorResponse as any);
  const today = new Date();
  return {
    showLevelQuestion: isBefore(levelTimestamp, subWeeks(today, 1)),
    showStressorQuestion: isBefore(stressorTimestamp, subMonths(today, 1)),
  };
}

const parseLocalDate = (d: string | Date): Date => {
  const date = new Date(d as any);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

interface ChartDataPoint { period: string; value: number }

function formatChartData(data: ChartDataPoint[]): Array<[string | number, number]> {
  if (data.length === 0) return [];
  const dates = data[0].period.split('..');
  const numDaysInBetween = differenceInCalendarDays(dates[1], dates[0]);
  const fmt = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;

  if (numDaysInBetween >= 360) {
    return data.map((d) => [parseLocalDate(d.period.split('..')[0]).getFullYear(), d.value]);
  }
  if (numDaysInBetween >= 27) {
    return data.map((d) => [
      parseLocalDate(d.period.split('..')[0]).toLocaleString('default', { month: 'long', year: 'numeric' }),
      d.value,
    ]);
  }
  if (numDaysInBetween >= 6) {
    return data.map((d) => {
      const [start, end] = d.period.split('..');
      return [`${fmt(parseLocalDate(start))}-${fmt(parseLocalDate(end))}`, d.value];
    });
  }
  return data.map((d) => [fmt(parseLocalDate(d.period.split('..')[0])), d.value]);
}

function toLocalYYYYMMDD(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const formatShortDate = (dateInput: Date | string | null | undefined): string => {
  if (!dateInput) return '';
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

function formatCompactNumber(num: number, options: { decimals?: number } = {}): string {
  const { decimals = 1 } = options;
  if (!num) return '';
  if (typeof num !== 'number') return String(num);
  try {
    const formatter = new Intl.NumberFormat('en-US', {
      notation: 'compact', compactDisplay: 'short', maximumFractionDigits: decimals,
    });
    return '$' + formatter.format(num);
  } catch (error) {
    console.error('Error formatting number:', error);
    return String(num);
  }
}

const validateUrl = (value: string): boolean => {
  if (!value) return true;
  try {
    const u = new URL(value);
    return ['http:', 'https:'].includes(u.protocol) || /^(sms:|tel:)/i.test(value);
  } catch {
    return /^(sms:|tel:)/i.test(value);
  }
};

const validatePhone = (value: string): boolean => {
  if (!value) return true;
  const digits = (value.match(/\d/g) || []).length;
  return digits >= 7 && /^[0-9+\s().-]+$/.test(value);
};

const validateEmail = (value: string): boolean => {
  if (!value) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

const getDefaultECFilters = (user: any): { 'License Type': string[] } => {
  const defaultFilters: { 'License Type': string[] } = { 'License Type': [] };
  const users_title_type = user.provider_title?.license_type;
  if (users_title_type === 'physician') defaultFilters['License Type'] = ['Physicians'];
  else if (users_title_type === 'nurse') defaultFilters['License Type'] = ['Nurses'];
  else if (users_title_type === 'advanced_practice_provider') defaultFilters['License Type'] = [];
  else if (users_title_type === 'other') defaultFilters['License Type'] = [];
  return defaultFilters;
};

function formatPhoneNumber(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 11) return '';
  const isEleven = digits.length === 11;
  const offset = isEleven ? 1 : 0;
  const countryCode = isEleven ? `${digits[0]} ` : '';
  const areaCode = digits.substring(offset, offset + 3);
  const prefix = digits.substring(offset + 3, offset + 6);
  const lineNumber = digits.substring(offset + 6, offset + 10);
  return `${countryCode}(${areaCode}) ${prefix}-${lineNumber}`.trim();
}

function ymdToMdyDashed(ymd: string | null | undefined): string {
  if (!ymd) return '';
  const parts = String(ymd).split('-');
  if (parts.length < 3) return '';
  const [y, m, d] = parts;
  return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
}

function isDemoEnv(): boolean { return window.location.hostname.startsWith('demo.'); }
function isProdEnv(): boolean { return window.location.hostname.startsWith('www.'); }


function downloadCsv(filename: string, headers: string[], rows: string[][]): void {
  const escape = (val: string) => `"${val.replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map((row) => row.map(escape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export {
  dateFormatter, isValidEmail, getColorGradient, hexToRgba, isPersonalEmail, personalEmailDomains,
  formatUnixTimestamp, formatIsoDate, sumContentLengths, isSingleSignOnEmail, interpolateColor,
  shuffleArray, shuffleArrayWithSeed, shouldShowSurvey, decodeEntities, convertTimeToSeconds,
  convertSecondsToTime, getCurrentBreakpoint, hasCommonItem, getDeviceType, forceReloadWithCacheBust,
  parseTimespanFilter, parseAgeFilter, round, formatChartData, showBurnoutQuestions, formatCompactNumber,
  toLocalYYYYMMDD, formatShortDate, formatIsoDateForFilename, validateEmail, validateUrl, validatePhone,
  getDefaultECFilters, formatPhoneNumber, ymdToMdyDashed, isDemoEnv, isProdEnv, downloadCsv,
  checkEmail, hashString, seededIndex,
};
export type { CheckEmailResult };
